<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            v-if="!customerFile.id"
            :label="__('customers.customer-files.file')"
            is-required
            :error="errors.file"
            :hint="form.file ? form.file.name : ''"
        >
            <DFileInput
                id="file"
                :custom="true"
                :multiple="false"
                :is-valid="isFormFieldValid('file')"
                @change="addFile"
            />
        </DFormRow>
        <DFormRow
            v-if="isPsaOrderSupported === true"
            :label="__('customers.customer-files.order')"
        >
            <DSelect
                id="order_id"
                v-model="form.order_id"
                :options="psaOrders"
                :search="true"
                :selection="true"
                @update:value="updateSelectedOrder"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.customer-files.contracts')"
        >
            <VueSelect
                v-model="form.contracts"
                :multiple="true"
                :close-on-select="false"
                :options="contracts"
                :filterable="false"
                :clearable="false"
                @open="onOpen"
                @close="onClose"
                @search="updateSearchText"
            >
                <template #list-footer>
                    <li
                        v-show="hasNextPage"
                        ref="load"
                        class="loader"
                    >
                        {{ __('messages.loading_more_options') }}
                    </li>
                </template>
                <template #no-options="{ searching }">
                    <template v-if="searching">
                        {{ __('messages.no_results') }}
                    </template>
                    <em
                        v-else
                        style="opacity: 0.5"
                    >
                        {{ __('messages.start_typing') }}
                    </em>
                </template>
            </VueSelect>
        </DFormRow>
        <DFormRow
            :label="__('customers.customer-files.description')"
            :error="errors.description"
        >
            <DFormTextarea
                id="description"
                v-model="form.description"
                rows="5"
                :is-valid="isFormFieldValid('description')"
                maxlength="255"
            />
        </DFormRow>

        <DFormControls
            :cancel-route="$route('customers.customers.customer-files.index', {customer: customerId})"
            :dirty="form.isDirty"
            :show-incident-list="customerFile.id ? false : true"
            :customer-id="customerFile.id ? 0 : customerId"
            @setIncident="setIncident"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>

import VueSelect from 'vue-select'

export default {
    name: 'FileForm',
    components: {
        VueSelect
    },
    props: {
        errors: {
            type: Object,
            default: () => {
                return {}
            }
        },
        customerId: {
            type: Number,
            default: 0
        },
        customerFile: {
            type: Object,
            default: () => {
                return {}
            }
        },
        psaOrders: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    emits: ['submit'],
    data () {
        return {
            form: this.$inertia.form({
                file: this.customerFile.file ?? null,
                description: this.customerFile.description ?? null,
                contracts: this.customerFile.contracts ?? [],
                order_id: this.customerFile.order_id ?? null,
                incident_id: null
            }),
            observer: null,
            searchText: null,
            currentPage: 1,
            lastPage: 1,
            contracts: []
        }
    },
    computed: {
        hasNextPage () {
            return this.currentPage < this.lastPage
        },
        isPsaOrderSupported () {
            return this.$page.props.isPsaOrderSupported
        }
    },
    mounted () {
        this.observer = new IntersectionObserver(this.appendContracts)
    },
    created () {
        this.fetchContractsInitially()
    },
    methods: {
        submitForm () {
            this.$emit('submit', this.form)
        },
        addFile (fileList) {
            this.form.file = fileList[0]
        },
        async onOpen () {
            if (this.hasNextPage) {
                await this.$nextTick()
                this.observer.observe(this.$refs.load)
            }
        },
        onClose () {
            this.observer.disconnect()
        },
        async fetchContractsInitially () {
            this.currentPage = 1

            await this.fetchContracts()
                .then((res) => {
                    this.lastPage = res.data.lastPage
                    this.contracts = res.data.items
                })
        },
        async appendContracts ([{ isIntersecting, target }]) {
            if (isIntersecting) {
                const ul = target.offsetParent
                const scrollTop = target.offsetParent.scrollTop

                this.currentPage++

                await this.fetchContracts()
                    .then((res) => {
                        this.contracts = this.contracts.concat(res.data.items)
                    })

                await this.$nextTick()
                ul.scrollTop = scrollTop
            }
        },
        fetchContracts () {
            const params = {
                customer: this.customerId,
                page: this.currentPage
            }

            if (this.searchText) {
                params.query = this.searchText
            }

            return window.axios.get(this.$route('customers.customers.contracts.searchContracts', params))
        },
        updateSearchText (query) {
            this.searchText = query

            if (this.timer) {
                clearTimeout(this.timer)

                this.timer = null
            }

            this.timer = setTimeout(() => {
                this.fetchContractsInitially()
            }, 500)
        },
        updateSelectedOrder (value) {
            this.form.order_id = value
        },
        setIncident (value) {
            this.form.incident_id = value
        }
    }
}
</script>

<style>
:root {
    --vs-selected-color: #5c6873 !important;
    --vs-state-disabled-bg: #e4e7ea !important;
    --vs-border-color: rgb(228, 231, 234) !important;
}
</style>
